import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiLink,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiChevronLeft, mdiPlus } from "@mdi/js";
import * as mdi from "@mdi/js";
import { Box, useTheme } from "@mui/material";
import { AddNewAssignmentTemplateDrawer } from "../components/AddNewAssignmentTemplateDrawer";
import { useAssignmentTemplateViewModel } from "../useAssignmentTemplateViewModel";

export function AssignmentTemplateList() {
  const router = useRouter();
  const { classes } = useStyles();
  const theme = useTheme();

  const {
    assignmentTemplates,
    stateManager,
    getAssignmentTemplates,
    openAddDrawer,
    setOpenAddDrawer,
    openDeleteModal,
    setOpenDeleteModal,
    selectedItem,
    setSelectedItem,
    onCreate,
    onDelete,
    onDeleteCancel,
    assignmentTemplateOptions,
    getResourceGroups,
    resourceGroups,
    getRoleOptions,
    roleOptions,
  } = useAssignmentTemplateViewModel();

  useEffect(() => {
    getAssignmentTemplates();
    getResourceGroups();
    getRoleOptions();
  }, []);

  const templateFieldset = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      groupable: false,
      flex: 1,
      renderCell: (p) => (
        <MiLink
          key={p.row.id}
          title={p.value}
          to={`/settings/order-management/assignment-templates/${p.row.id}/Details`}
        />
      ),
    },
    {
      field: "shortName",
      headerName: "Short Name",
      groupable: false,
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      field: "type",
      headerName: "Type",
      groupable: false,
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      field: "geoFenceDistance",
      headerName: "GeoFence Distance",
      groupable: false,
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      headerName: "Color",
      field: "color",
      renderCell: ({ value }) => <input type="color" value={value} disabled />,
    },
  ];

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.DELETE,
      onClick: () => {
        setSelectedItem(row);
        setOpenDeleteModal(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          size={1}
          color={theme.palette.red.main}
        />
      ),
    },
  ];

  const customOnBack = () => {
    router.navigate("/settings/order-management");
  };

  return (
    <Box className={classes.root}>
      <MiDetailPageHeader
        title="Assignment Templates"
        titleIcon={mdiChevronLeft}
        customOnBack={customOnBack}
        loading={stateManager.isBusy()}
        actionComponent={
          <MiButton
            title="Add Assignment Template"
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{
              path: mdiPlus,
              iconSize: 0.8,
            }}
            data-testid="add-assignment-template-btn"
            onClick={() => setOpenAddDrawer(true)}
          />
        }
      />
      <MiBox>
        <MiList
          columns={templateFieldset}
          data={assignmentTemplates}
          paginationMode={PAGINATION.CLIENT}
          loading={stateManager.isBusy()}
          getRowActions={getRowActions}
          disableRowSelectionOnClick
          data-testid="assignment-templates-table"
        />
      </MiBox>
      {openAddDrawer && (
        <AddNewAssignmentTemplateDrawer
          open={openAddDrawer}
          onClose={() => setOpenAddDrawer(false)}
          onSubmit={onCreate}
          loading={stateManager.isBusy()}
          assignmentTemplateOptions={assignmentTemplateOptions}
          resourceGroups={resourceGroups}
          roleOptions={roleOptions}
        />
      )}
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        description={`Are you sure you want to delete the Assignment Template: ${selectedItem?.name}?`}
        open={openDeleteModal}
        handlePositive={onDelete}
        handleNegative={onDeleteCancel}
        className={classes.modal}
        textPositive={BUTTON_LABELS.DELETE}
      />
    </Box>
  );
}

const useStyles = makeStyles()(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "inherit",
  },
  modal: {
    minWidth: 400,
  },
}));
